import React from 'react'
import { AccessibleIcon } from '@radix-ui/react-accessible-icon'
import { FloraVariants, forwardRef, styled } from '@grupoboticario/flora-react-system'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'
import { Text } from '@grupoboticario/flora-react-text'
import { Flex } from '@grupoboticario/flora-react-flex'

import { InvalidIcon, ValidIcon } from './lib/components'
import { useMask, MaskProps } from './lib/useMask'

const tokenDisabledAltContent = '$disabledAltContent'
const cssTranslateY = 'translateY(-10px)'

const StyledInput = styled('input', {
  appearance: 'none',
  borderWidth: '0',
  boxSizing: 'border-box',
  fontSize: '$bodySmall',
  margin: 0,
  outline: 'none',
  width: '100%',
  maxWidth: '100%',
  height: 48,
  minHeight: 48,
  transition: 'box-shadow .2s ease-in-out',
  fontFamily: 'inherit',
  webkitTapHighlightColor: 'rgba(0,0,0,0)',
  pointerEvents: 'visible',
  '$$input-padding-label-top': '25px',

  '&::before': {
    boxSizing: 'border-box',
  },
  '&::after': {
    boxSizing: 'border-box',
  },

  // Custom
  backgroundColor: '$backgroundPrimary',
  boxShadow: 'inset 0 0 0 1px $colors$nonInteractiveOutline',
  borderRadius: '$small',
  color: '$nonInteractivePredominant',
  fontVariantNumeric: 'tabular-nums',
  $$boxShadow: 'inset 0px 0px 0px 2px $colors$actionableDefault',

  '&:-webkit-autofill': {
    boxShadow: '$$boxShadow',
  },

  '&:-webkit-autofill::first-line': {
    color: '$actionableDefault',
    fontSize: '$bodySmall',
  },

  '&::placeholder': {
    color: 'transparent',
  },

  '& + label': {
    color: '$nonInteractivePredominant',
  },

  'textarea& + label': {
    fontSize: '$bodySmall',
    width: 'calc(100% - 28px)',
    backgroundColor: 'white',
    padding: '7px 0 2px',
    top: '12px',
    borderRadius: '$small',
    transform: 'translateY(-1px)',
    '& alt + label, &:focus + label': {
      color: '$actionableAlt',
    },
  },

  'textarea&:not(:placeholder-shown), textarea:-webkit-autofill': {
    paddingTop: 22,
  },
  'input&:not(:placeholder-shown), input:-webkit-autofill': {
    paddingTop: 17,
  },
  'input&:not(:placeholder-shown) + label, input:-webkit-autofill + label, textarea&:not(:placeholder-shown) + label, textarea:-webkit-autofill + label':
    {
      color: '$nonInteractiveAuxiliar',
      transform: cssTranslateY,
      fontSize: '$auxiliar',
      '& span[data-label-placeholder]': { display: 'none' },
    },

  '&:focus': {
    paddingTop: 17,
    boxShadow: '$$boxShadow, $downMid',
    borderRadius: '$small',
    '&:-webkit-autofill': {
      boxShadow: '$$boxShadow',
    },
    '& + label': {
      color: '$actionableDefault',
      transform: cssTranslateY,
      fontSize: '$auxiliar',
      '& span[data-label-placeholder]': { display: 'none' },
    },
  },

  '&[aria-invalid]': {
    $$boxShadow: 'inset 0px 0px 0px 2px $colors$error-standard',
    boxShadow: '$$boxShadow',
    '&:focus': {
      boxShadow: '$$boxShadow, $downMid',
    },
  },

  '&[data-valid="true"]': {
    $$boxShadow: 'inset 0px 0px 0px 2px $colors$success-standard',
    boxShadow: '$$boxShadow',
    '&:focus': {
      boxShadow: '$$boxShadow, $downMid',
    },
  },

  variants: {
    focused: {
      true: {
        '& + label': {
          color: '$actionableDefault',
        },
        '& + focus': {
          color: '$actionableAlt',
        },
        boxShadow: '$$boxShadow, $downMid',
      },
    },
    filled: {
      true: {
        '&:focus': {
          paddingTop: '$$input-padding-label-top',
        },
        paddingRight: 40,
        paddingTop: '$$input-padding-label-top',
        '& + label': {
          transform: cssTranslateY,
          fontSize: '$auxiliar',
          '& span[data-label-placeholder]': { display: 'none' },
        },
      },
    },
    variant: {
      standard: {},
      alt: {
        color: '$nonInteractiveAltPredominant',
        boxShadow: 'inset 0 0 0 1px $colors$nonInteractiveAltOutline',
        '& + label, &:focus + label': {
          color: '$nonInteractiveAltPredominant',
          backgroundColor: '$backgroundAltSecondary',
        },
        '& > span': {
          color: '$nonInteractiveAltAuxiliar',
          backgroundColor: '$backgroundAltSecondary',
        },
        'input&:not(:placeholder-shown), textarea&:not(:placeholder-shown)': {
          '& + label': {
            color: '$nonInteractiveAltAuxiliar',
            backgroundColor: '$backgroundAltSecondary',
          },
        },
        'textarea& + label': {
          backgroundColor: '$backgroundAltSecondary',
          color: '$actionableAlt',
        },
        backgroundColor: '$backgroundAltSecondary',
        $$boxShadow: 'inset 0px 0px 0px 2px $colors$actionableAlt',
        '& span[data-label-required]': {
          color: '$nonInteractiveAltAuxiliar',
        },
      },
    },
    disabled: {
      true: {
        pointerEvents: 'none',
        fontSize: '$bodySmall',
        cursor: 'not-allowed',
        paddingTop: '$$input-padding-label-top',
        backgroundColor: '$disabledBackground',
        color: '$disabledContent',
        '& + label, &:focus + label': {
          color: 'red',
        },
        '& + label, & + span': {
          color: '$disabledContent',
          backgroundColor: 'transparent',
        },
        'textarea& + label': {
          backgroundColor: '$disabledBackground',
          '& > span': {
            color: '$disabledContent',
          },
        },
        'input&:not(:placeholder-shown) + label, input:-webkit-autofill + label, textarea&:not(:placeholder-shown) + label, textarea:-webkit-autofill + label':
          {
            color: '$disabledContent',
          },
      },
    },
  },
  compoundVariants: [
    {
      disabled: true,
      filled: true,
      css: {
        '& + label': {
          transform: cssTranslateY,
          fontSize: '$auxiliar',
          '& span[data-label-placeholder]': { display: 'none' },
        },
      },
    },
    {
      variant: 'alt',
      disabled: true,
      css: {
        backgroundColor: '$disabledAltBackground',
        boxShadow: '$downMid',
        color: tokenDisabledAltContent,
        '& + label, & + span': {
          color: tokenDisabledAltContent,
          backgroundColor: 'transparent',
        },
        'input&:not(:placeholder-shown), textarea&:not(:placeholder-shown)': {
          '& + label': {
            color: tokenDisabledAltContent,
            backgroundColor: 'transparent',
          },
        },
      },
    },
    {
      variant: 'alt',
      focused: true,
      css: {
        '& + label': {
          color: '$nonInteractiveAltPredominant',
        },
      },
    },
  ],
})

const FloatingLabel = styled('label', {
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  left: 14,
  top: 18,
  fontSize: '$bodySmall',
  lineHeight: '13px',
  pointerEvents: 'none',
  transformOrigin: '0 0',
  transition: '.1s ease-in-out',
  transitionProperty: 'transform, font-size',
  whiteSpace: 'nowrap',
  '& span[data-label-required]': {
    color: '$error-standard',
    marginRight: '$1',
  },
  '& span[data-label-placeholder]': { color: '$nonInteractiveAuxiliar' },
  '& span[data-label-optional]': {
    color: '$nonInteractiveAuxiliar',
    marginLeft: '$1',
    marginRight: '$1',
  },
  variants: {
    disabled: {
      true: {
        '& span[data-label-placeholder],& span[data-label-optional], & span[data-label-required]': {
          color: '$disabledContent',
        },
      },
    },
    variant: {
      standard: {},
      alt: {
        '& span[data-label-required]': {
          color: '$error-alt',
        },
      },
    },
  },
  compoundVariants: [
    {
      variant: 'alt',
      disabled: true,
      css: {
        '& span[data-label-placeholder],& span[data-label-optional], & span[data-label-required]': {
          color: '$nonInteractiveAltAuxiliar',
        },
      },
    },
    {
      variant: 'alt',
      disabled: false,
      css: {
        '& span[data-label-optional]': {
          color: '$nonInteractiveAltAuxiliar',
        },
        '& span[data-label-placeholder]': { color: '$nonInteractiveAltAuxiliar' },
      },
    },
  ],
})

const FormControl = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  width: '100%',
  position: 'relative',
  fontFamily: 'inherit',
  minHeight: 67,
  '& div[data-flora-icon="left"], & div[data-flora-icon="right"]': {
    width: 48,
    height: 48,
    pointerEvents: 'none',
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    top: 0,
    [`& ~ ${FloatingLabel}`]: {
      left: 43,
    },
  },
  '& div[data-flora-icon="left"]': {
    left: 0,
  },
  '& div[data-flora-icon="right"]': {
    right: 0,
  },
  variants: {
    variant: {
      alt: {},
      standard: {},
    },
    errorSpacing: {
      false: {
        minHeight: 48,
      },
    },
  },
})

const AuxiliarText = styled(Text, {
  display: 'flex',
  alignItems: 'center',
  marginTop: '$1',
  fontSize: '$auxiliar',
  height: 16,
  svg: { marginRight: '$1', height: 16 },
  variants: {
    alt: {
      true: {
        color: '$backgroundSecondary',
      },
    },
  },
})

/**
 * @deprecated Esta tipagem refere-se a um componente que será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florainput-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export type InputProps = HTMLFloraProps<'input'> &
  FloraVariants<typeof StyledInput> & {
    id: string
    labelText: string
    optionalText?: string
    leftIcon?: React.ReactElement
    rightIcon?: React.ReactElement
    placeholder?: string
    invalidText?: string
    validText?: string
    invalid?: boolean
    errorSpacing?: boolean
    required?: boolean
    'data-valid'?: boolean
    mask?: MaskProps
    value?: any
    defaultValue?: any
  }

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florainput-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const Input = forwardRef<'input', InputProps>((props, forwardedRef) => {
  const {
    id,
    invalidText,
    labelText,
    optionalText = '',
    validText,
    invalid = false,
    errorSpacing = true,
    placeholder,
    required = false,
    leftIcon,
    rightIcon,
    variant,
    css,
    mask,
    onChange,
    onKeyDown,
    value,
    defaultValue,
    ...rest
  } = props

  const labelId = id + '-label-msg'
  const errorId = id + '-error-msg'
  const validId = id + '-success-msg'

  rest['aria-labelledby'] = labelId

  if (invalid) {
    rest['aria-invalid'] = invalid
    if (invalidText) rest['aria-describedby'] = errorId
  }

  if (rest.disabled) {
    rest['aria-disabled'] = true
    rest['tabIndex'] = -1
  }

  const dataValid = rest['data-valid']

  if (dataValid && validText) {
    rest['aria-describedby'] = validId
  }

  const maskProps = useMask({
    ref: forwardedRef,
    mask,
    value,
    defaultValue,
    onChange,
    onKeyDown,
  })

  const isDisabled = rest.disabled === true
  const isVariantAlt = variant === 'alt'

  let resultVariant = ''

  if (isDisabled) {
    if (isVariantAlt) {
      resultVariant = '$disabledAltContent'
    } else {
      resultVariant = '$disabledContent'
    }
  } else if (isVariantAlt) {
    resultVariant = '$actionableAlt'
  } else {
    resultVariant = '$actionableDefault'
  }

  let isColorText = ''

  if (variant === 'alt') {
    isColorText = '$nonInteractiveAltPredominant'
  } else {
    isColorText = '$nonInteractivePredominant'
  }

  return (
    <FormControl errorSpacing={errorSpacing} variant={variant} data-flora="input-wrapper">
      {leftIcon && (
        <Flex css={{ color: resultVariant }} data-flora-icon="left">
          <AccessibleIcon label={labelText}>{leftIcon}</AccessibleIcon>
        </Flex>
      )}
      <StyledInput
        {...maskProps}
        data-flora="input"
        id={id}
        required={!!required}
        placeholder={placeholder || labelText}
        variant={variant}
        css={{
          ...(css || {}), // nosonar
          paddingLeft: leftIcon ? 43 : 14, // nosonar
          paddingRight: rightIcon ? 43 : 14, // nosonar
        }}
        {...rest}
      />
      {labelText && (
        <FloatingLabel htmlFor={id} id={labelId} disabled={rest.disabled} variant={variant}>
          {labelText}
          {required && <Text data-label-required>*</Text>}
          {!required && <Text data-label-optional>{optionalText}</Text>}
          {placeholder && <Text data-label-placeholder>{placeholder}</Text>}
        </FloatingLabel>
      )}
      {rightIcon && (
        <Flex css={{ color: resultVariant }} data-flora-icon="right">
          <AccessibleIcon label={labelText}>{rightIcon}</AccessibleIcon>
        </Flex>
      )}
      {invalid && invalidText && (
        <AuxiliarText id={errorId} color={isColorText}>
          <InvalidIcon />
          {invalidText}
        </AuxiliarText>
      )}
      {dataValid && validText && (
        <AuxiliarText id={validId} color={isColorText}>
          <ValidIcon />
          {validText}
        </AuxiliarText>
      )}
    </FormControl>
  )
})
